import { FlexColumn, Text } from 'components'
import { type Id, type ToastOptions, toast } from 'react-toastify'
import { sets } from 'abis/set/sets'
import type { Transaction } from 'services'

import FailIcon from 'assets/fail-transaction-icon.svg?react'
import { GLOBAL_COLORS } from 'theme'
import PendingIcon from 'assets/pending-transaction-icon.svg?react'
import type React from 'react'
import SuccessIcon from 'assets/success-transaction-icon.svg?react'
import { getBlockExplorerName, transactionAmountFormatter } from './service'
import styled from 'styled-components'
import { TransactionStatus } from 'sdk/omni/omni-transaction-service/types'

export type NotificationActionTypes =
  | 'approve'
  | 'buy'
  | 'sell'
  | 'swap'
  | 'mint'
  | 'redeem'
  | 'stake'
  | 'unstake'
  | 'copied'
  | 'withdraw'
  | 'omniRedeem'
  | 'retry'
type NotificationStatusTypes = TransactionStatus

export const SnackbarClose = styled.button`
  flex-shrink: 0;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 36px;
  height: 36px;
  padding: 8px;
  margin: 0 8px 0 0;
  cursor: pointer;
  position: relative;
  pointer-events: auto;
  -webkit-tap-highlight-color: ${GLOBAL_COLORS.transparent};
  /* Reset button styles */
  outline: none;
  background-color: ${GLOBAL_COLORS.transparent};
  border: none;
  /* SVG icon properties */
  font-size: 12px;
  color: ${GLOBAL_COLORS.white};
  align-self: center;
`
const TitleText = styled(Text)``

const LinkText = styled(Text)`
  text-decoration: underline;
`

interface NotificationOptions extends ToastOptions {
  childElement?: React.ReactNode
  templateFactory?: (text: string, Children?: React.ReactNode) => JSX.Element
}

const goToEtherscan = (txId: string, network: number) => {
  window.open(`${sets[network].blockExplorer}/tx/${txId}`)
}

const textSwitch = (transaction: Transaction): string => {
  const { action, firstSymbol, firstValue, secondSymbol, secondValue } =
    extractTransactionDetails(transaction)
  switch (action) {
    case 'approve':
      return `Approve ${firstValue} ${firstSymbol}`
    case 'swap':
      return `Swap ${secondValue} ${secondSymbol} ${
        Number(firstValue) > 0 ? `for ${firstValue} ${firstSymbol}` : ` for ${firstSymbol}`
      } `
    case 'mint':
      return `Mint ${
        Number(firstValue) > 0 ? firstValue : ''
      } ${firstSymbol} with ${secondValue} ${secondSymbol}`
    case 'redeem':
      return `Redeem ${firstValue} ${firstSymbol} ${
        Number(secondValue) > 0 ? `with ${secondValue} ${secondSymbol}` : ''
      } `
    case 'stake':
      return `Stake ${secondValue} ${secondSymbol}`
    case 'unstake':
      return `Unstake ${secondValue} ${secondSymbol}`
    case 'buy':
      return `Buy ${firstValue} ${firstSymbol}`
    case 'sell':
    case 'omniRedeem':
      return `Sell ${firstValue} ${firstSymbol}`
    case 'withdraw':
      return `Withdraw from ${firstSymbol}`
    case `retry`:
      return `Retry transaction`
    default:
      return ''
  }
}

const iconSwitch = (status: NotificationStatusTypes) => {
  switch (status) {
    case TransactionStatus.INFLIGHT:
      return PendingIcon
    case TransactionStatus.DELIVERED:
      return SuccessIcon
    case TransactionStatus.FAILED:
      return FailIcon
    default:
      return PendingIcon
  }
}

/**
 * Standard template factory used by all notifications, different one can be specified @see NotificationOptions
 * @param text
 * @param Children
 * @returns
 */
export const getNotificationTemplate = (
  text: string,
  Children: React.ReactNode = null,
): JSX.Element => (
  <FlexColumn>
    <TitleText type={'p'}>{text}</TitleText>
    <>{Children}</>
  </FlexColumn>
)

/**
 * Notification with the least parameters required
 * @param text The text to display
 * @param options extension of Toast options
 * @See NotificationOptions
 * @returns
 */
export const genericNotify = (text: string, options: NotificationOptions): React.ReactText => {
  const { childElement, templateFactory } = options
  const factory = templateFactory ? templateFactory : getNotificationTemplate
  return toast(factory(text, childElement || null), options)
}

/**
 * Notification with the least parameters required
 * @param text The text to display
 * @param options extension of Toast options
 * @See NotificationOptions
 * @returns
 */
export const genericUpdate = (text: string, options: NotificationOptions): void => {
  const { childElement, templateFactory } = options
  const factory = templateFactory ? templateFactory : getNotificationTemplate
  const updateOptions = { ...{ render: factory(text, childElement || null) }, ...options }
  // if user has dismissed, create a new one, or update existing
  if (!toast.isActive(options.toastId as Id)) {
    genericNotify(text, options)
  } else {
    toast.update(options.toastId as Id, updateOptions)
  }
}

/**
 * HoC method that adds Icon options to the generic notification
 * @param icon @see NotificationStatusTypes
 * @param isDarkMode
 * @param text The text to display
 * @param childElement optional
 * @param options optional
 * @returns
 */
export const notifyWithIcon = (
  icon: NotificationStatusTypes,
  isDarkMode: boolean,
  text: string,
  options?: NotificationOptions,
): React.ReactText => {
  const CustomIcon = iconSwitch(icon)
  const additionalOptions = {
    icon: (
      <CustomIcon stroke={isDarkMode ? GLOBAL_COLORS.cautiousBlue : GLOBAL_COLORS.rurikonBlue} />
    ),
  }
  const mergedOptions: NotificationOptions = {
    ...additionalOptions,
    ...(options ? options : {}),
  }
  return genericNotify(text, mergedOptions)
}

export const notifyTransactionError = () => toast('Something went wrong. Please try again')

export const notifyTransactionLiquidityError = (id: Id) =>
  toast('Low liquidity, please try again', { toastId: id })

export const updateWithIcon = (
  icon: NotificationStatusTypes,
  isDarkMode: boolean,
  text: string,
  options?: NotificationOptions,
): void => {
  const CustomIcon = iconSwitch(icon)
  const additionalOptions = {
    icon: (
      <CustomIcon stroke={isDarkMode ? GLOBAL_COLORS.cautiousBlue : GLOBAL_COLORS.rurikonBlue} />
    ),
  }
  const mergedOptions: NotificationOptions = {
    ...additionalOptions,
    ...(options ? options : {}),
  }
  genericUpdate(text, mergedOptions)
}

export const getNotificationText = (transaction: Transaction): string => textSwitch(transaction)

interface TransactionToastProps {
  status: TransactionStatus
  action: NotificationActionTypes
  firstSymbol: string
  firstValue: string
  secondSymbol: string
  secondValue: string
  hash: string
  network: number
}

export interface SymbolsAndValuesProps {
  firstSymbol: string
  firstValue: string
  secondSymbol: string
  secondValue: string
}

export const getSymbolsAndValues = (transaction: Transaction): SymbolsAndValuesProps => {
  const firstSymbol =
    transaction.action.indexDetails !== null ? transaction.action.indexDetails.indexTicker : ''
  const firstValue =
    transaction.action.indexDetails !== null
      ? transactionAmountFormatter(transaction.action.indexDetails.value)
      : ''
  const secondSymbol =
    transaction.action.stakingDetails !== null ? transaction.action.stakingDetails.assetName : ''
  const secondValue =
    transaction.action.stakingDetails !== null
      ? transactionAmountFormatter(transaction.action.stakingDetails.value)
      : ''

  return {
    firstSymbol,
    firstValue,
    secondSymbol,
    secondValue,
  }
}

const extractTransactionDetails = (transaction: Transaction): TransactionToastProps => {
  const { firstSymbol, firstValue, secondSymbol, secondValue } = getSymbolsAndValues(transaction)

  let network = 1
  if (!transaction.action.network) {
    console.log('No network specified for transaction, using default')
  } else {
    network = transaction.action.network
  }

  return {
    status: transaction.status,
    action: transaction.action.action,
    firstSymbol,
    firstValue,
    secondSymbol,
    secondValue,
    hash: transaction.replacementHash ? transaction.replacementHash : transaction.hash,
    network,
  }
}

const buildBlockExplorerLink = (transaction: Transaction): React.ReactNode => {
  const { hash, network } = extractTransactionDetails(transaction)
  return (
    <LinkText type={'four'} onClick={() => goToEtherscan(hash, network)}>
      View on {getBlockExplorerName(network)}
    </LinkText>
  )
}

/**
 * Method that renders transactional information as a notification
 * @returns
 */
export const notifyTransactionToast = (
  transaction: Transaction,
  isDarkMode: boolean,
): React.ReactText => {
  const text = textSwitch(transaction)
  return notifyWithIcon(transaction.status, isDarkMode, text, {
    childElement: buildBlockExplorerLink(transaction),
  })
}

/**
 * Call notify transaction but pass in the hash as the toast id
 * @param transaction the transaction object
 * @param isDarkMode
 * @returns
 */
export const updateTransactionToast = (
  transaction: Transaction,
  isDarkMode: boolean,
  options?: ToastOptions,
): void => {
  const text = textSwitch(transaction)
  const mergedOptions: NotificationOptions = {
    ...{
      childElement: buildBlockExplorerLink(transaction),
      toastId: transaction.hash,
      autoClose: 3000,
    },
    ...(options ? options : {}),
  }
  updateWithIcon(transaction.status, isDarkMode, text, mergedOptions)
}

/**
 * Create a tx toast
 * @param transaction the transaction object
 * @param isDarkMode
 * @param toastId optional toast id is used to determine if the toast should be updated or created
 * @returns
 */
export const notifyTransactionReceipt = (
  transaction: Transaction,
  isDarkMode: boolean,
): React.ReactText => {
  const text = textSwitch(transaction)
  return notifyWithIcon(transaction.status, isDarkMode, text, {
    childElement: buildBlockExplorerLink(transaction),
    autoClose: false,
    toastId: transaction.hash,
  })
}
